import { useEffect, useRef, useState } from 'react';
import { message as antdMessage } from 'antd';
import BaselineRecording from './OdorIdentifiacationSteps/BaselineRecording';
import AnalyteRecording from './OdorIdentifiacationSteps/AnalyteRecording';
import OdorDisplay from './OdorIdentifiacationSteps/OdorDisplay';
import SensorCleaning from './OdorIdentifiacationSteps/SensorCleaning';
import { Mutex, MutexInterface, withTimeout } from 'async-mutex';
import { CSM_PROTOCOL_COMMAND_TYPE, CSM_PROTOCOL_EVENT_TYPE } from '../../components/serial/csm';
import { v4 as uuidv4 } from 'uuid';
import { loadSpotsGrid1D, loadModel } from '../../services/cache/localStorage';
import { DEFAULT_ANALYSIS_COMPARISON_THRESHOLD, DEFAULT_ANALYTE_DURATION_SEC, DEFAULT_PLOT_DECIMATED_FPS } from '../../utils/constants/constants';
import { parseBiosensorsSignalMessagePayload, updateKineticSeries, shouldCompute, computeEnvelope, correctHumidity } from '../../components/analysis/compute';
import { computeSignatureFromSignal, DEFAULT_SIGNATURE_PROCESSING_PARAMS } from '../../components/analysis/signature';
import { processMziData } from '../../components/analysis/mzi';
import { initQuestioningStateInfos, QuestioningState, QuestioningStateInfo, updateStateFixedAnalyteDuration } from '../../components/analysis/odorstates';
import { GenericModelV2, ModelCategory } from '../../components/analysis/definitions';
import { QuestionningResult, SignatureWithSpotgrid } from '../../types/types';
import { classifySignatureChemical, classifySignatureWithModel } from '../../components/analysis/classifier';
import { Row } from 'antd';
import { Paper } from '../../components/common/common';
import { useMessageContext } from '../../state/context/MessageContext';
import BubbleChart, { SecondData } from '../../components/widgets/ChemicalFamilyIdentification/BubbleChart';
import GlobalParameters from '../../components/widgets/Parameters/GlobalParameters';
import ResetToZero from '../../components/widgets/Parameters/ResetToZero';
import GlobalParametersIdentificationComponent from './OdorIdentificationComponents/GlobalParametersComponent';
import { instanceOfGenericModelV2Type } from '../../utils/helpers/model';
import { getModelPreprocessingParams } from '../../components/analysis/models';

type CsmOdorIdentificationProps = {};

export const CsmOdorIdentification: React.FC<CsmOdorIdentificationProps> = () => {
  const { csmMessages, csmFwVersion, consumeCSMMessage, clearCSMMessages, addCSMCommand, hihValues } = useMessageContext();

  const [currentSpotsgrid1d, setCurrentSpotsgrid1d] = useState<number[] | null>(null);

  // for acquisition
  const messageQueueMutexRef = useRef<MutexInterface>(withTimeout(new Mutex(), 300));
  const lastDecimationTickRef = useRef<number>(0);
  const rawMZISeriesRef = useRef<number[][]>([]);
  const isMZIcomputingrequested = useRef<boolean>(false);

  // Corrections
  const firstMZIsRef = useRef<number[] | null>(null);
  const [humidityCompensationEnabled, sethumidityCompensationEnabled] = useState<boolean>(false);
  const [humidityCalibrant, setHumidityCalibrant] = useState<SignatureWithSpotgrid | undefined>(undefined);
  const humidityBaselineRef = useRef<number | null>(null);

  // signature and prediction computation
  const signalMZI = useRef<number[]>([]);
  const analyteMZISeriesRef = useRef<number[][]>([]);
  const [questionningSignature, setQuestionningSignature] = useState<number[] | null>(null);
  const [questionningSpotsgrid1d, setQuestionningSpotsgrid1d] = useState<number[] | null>(null);
  const [currentModel, setCurrentModel] = useState<GenericModelV2 | null>(null);
  const [questionningResult, setQuestionningResult] = useState<QuestionningResult | null>(null);
  const [currentProba, setCurrentProba] = useState<SecondData[]>([]); // chemical bubble display

  // state update and display
  const signalEnvelopeAvgRef = useRef<number>(0);
  const questioningStateInfo = useRef<QuestioningStateInfo>(initQuestioningStateInfos());
  const averageMZISeriesRef = useRef<number[]>([]);
  const decimatedMZISeriesCorrected = useRef<number[][]>([]);
  const humidityDisplayed = useRef<number>(0);
  const temperatureDisplayed = useRef<number>(0);

  // various parameters
  const [showGlobalParameters, setShowGlobalParameters] = useState<boolean>(false);
  const [showPredictionResult, setShowPredictionResult] = useState<boolean>(false);
  const [signatureWindowValue, setSignatureWindowValue] = useState(DEFAULT_ANALYTE_DURATION_SEC);
  const [thresholdOverride, setThresholdOverride] = useState(DEFAULT_ANALYSIS_COMPARISON_THRESHOLD);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // reset MZI for display centered around 0
  const onClickReset = () => {
    setQuestionningSignature(null);
    questioningStateInfo.current = initQuestioningStateInfos();

    rawMZISeriesRef.current = [];
    firstMZIsRef.current = null;

    signalEnvelopeAvgRef.current = 0;
    analyteMZISeriesRef.current = [];

    setQuestionningResult(null);
    setShowPredictionResult(false);
  };

  // build analyte buffer and compute signature when buffer ready
  // this function works with a user-defined duration parameter to control analyte length
  const buildSignatureFixedAnalyteDuration = (previousQuestioningState: QuestioningState) => {
    if (questioningStateInfo.current.state === QuestioningState.AnalyteRecording) {
      // capture frames once analyte is started during a specified signatureWindowValue amount of time
      if (questioningStateInfo.current.startTimestamp > 0 && Date.now() - questioningStateInfo.current.startTimestamp < signatureWindowValue * 1000) {
        analyteMZISeriesRef.current.push(signalMZI.current);
      }
    } else if (previousQuestioningState === QuestioningState.AnalyteRecording) {
      constructSignature();
      analyteMZISeriesRef.current = [];
    }
  };

  useEffect(() => {
    clearCSMMessages();
    addCSMCommand({
      id: uuidv4().toString(),
      message: {
        CmdType: CSM_PROTOCOL_COMMAND_TYPE.StartSampling,
      },
    });
    setIsLoading(false);
    return () => {
      addCSMCommand({
        id: uuidv4().toString(),
        message: {
          CmdType: CSM_PROTOCOL_COMMAND_TYPE.StopSampling,
        },
      });
      setIsLoading(true);
    };
  }, []);

  useEffect(() => {
    let _spotsgrid1d = loadSpotsGrid1D();
    if (!_spotsgrid1d) {
      console.log('sense page: spotsgrid1d is empty');
      return;
    }
    setCurrentSpotsgrid1d(_spotsgrid1d);
  }, []);

  useEffect(() => {
    if (csmMessages.length === 0) {
      return;
    }
    if (messageQueueMutexRef.current.isLocked()) {
      return;
    }
    // console.log("sense page: acquiring mutex..")
    if (!currentSpotsgrid1d) {
      console.log('odor identification page: spotsgrid is empty');
      return;
    }

    // implement me : unfoldBiosensorEvent

    messageQueueMutexRef.current
      .acquire()
      .then((release) => {
        let nFramesProcessed = 0;

        csmMessages.forEach((message) => {
          if (message.message.Type !== CSM_PROTOCOL_EVENT_TYPE.BiosensorsSignalEvent) {
            console.log('sense page: csm ble message is not a biosensors signal event', message.message);
            consumeCSMMessage(message.id);
            return;
          } else {
            nFramesProcessed++;
            consumeCSMMessage(message.id);

            // ------ Retrieve raw data from the device ------
            // -----------------------------------------------
            let mzis = parseBiosensorsSignalMessagePayload(message, csmFwVersion);
            if (!mzis) return;

            if (firstMZIsRef.current === null) {
              firstMZIsRef.current = [...mzis];
            }
            rawMZISeriesRef.current.push(mzis);

            // set Baseline delta H for future use
            if (humidityBaselineRef.current === null) {
              console.log('Changing humidity baseline reference as ', hihValues.humidity);
              humidityBaselineRef.current = hihValues.humidity;
            }

            // ------ Process and get last decimated MZI frame ------
            // ------------------------------------------------------
            isMZIcomputingrequested.current = shouldCompute(message.ts, lastDecimationTickRef); // shouldCompute is in fact decimation

            // decimation to prevent frequent rendereing
            if (isMZIcomputingrequested.current) {
              // Humidity correction not applied here because we want to extract the firstMZI-substracted MZI for the signature computation
              let MziProcessingResult = processMziData(rawMZISeriesRef.current, firstMZIsRef.current, currentSpotsgrid1d, false, undefined, null, hihValues);
              rawMZISeriesRef.current = [];

              if (MziProcessingResult === undefined) return;
              let [, , decimatedMZI] = MziProcessingResult;

              // Note : you can add any further processing on decimated MZI here to compute some correctedMZI (for gauge display, or state updates) !
              // Just keep in mind the input for questioning signature computation is NOT the correctedMZI, but always the decimatedMZI
              // Indeed, the questioning signature has its own processing pipeline and we don't want to apply multiple or partial corrections (when using correctedMZI)
              let correctedMZI = [...decimatedMZI];
              if (humidityCompensationEnabled && humidityCalibrant !== undefined && humidityBaselineRef.current !== null && currentSpotsgrid1d !== null) {
                correctedMZI = correctHumidity(decimatedMZI, hihValues.humidity - humidityBaselineRef.current, currentSpotsgrid1d, humidityCalibrant);
              }

              let [averageMZI, ,] = computeEnvelope(correctedMZI, currentSpotsgrid1d);
              updateKineticSeries(averageMZI, correctedMZI, averageMZISeriesRef, decimatedMZISeriesCorrected);

              // console.log('averageMZI...', averageMZI, ' humidity correction is', humidityCompensationEnabled, 'lastDecimationTickRef.current is', lastDecimationTickRef.current)

              // Update displayed values
              signalEnvelopeAvgRef.current = averageMZI; // for display in the component
              signalMZI.current = decimatedMZI;
              humidityDisplayed.current = hihValues.humidity;
              temperatureDisplayed.current = hihValues.temperature;

              // update algo state
              let previousQuestioningState = questioningStateInfo.current.state;
              updateStateFixedAnalyteDuration(questioningStateInfo.current, averageMZI, averageMZISeriesRef.current, signatureWindowValue);

              // handle signature computation logic here
              // this ensures the signature processing to be decorrelated from the state machine update
              buildSignatureFixedAnalyteDuration(previousQuestioningState);
            }
          }
        });
        // console.log('processed nFramesOnOneMutexLock', nFramesOnOneMutexLock)
        release();
      })
      .catch((e: any) => {
        console.log('sense page: could not acquire mutex', e);
        messageQueueMutexRef.current.cancel();
        messageQueueMutexRef.current.release();
      });
    return () => {
      messageQueueMutexRef.current.cancel();
      messageQueueMutexRef.current.release();
    };
  }, [csmMessages]);

  useEffect(() => {
    let _model = loadModel();
    if (_model === null) return;
    if (!instanceOfGenericModelV2Type(_model)) {
      antdMessage.error(`Old model detected, please migrate it to the new structure !`);
      return;
    }
    console.log(' Selected Model is :', _model);
    if (_model?.data.type === ModelCategory.ComparisonSignatures || _model?.data.type === ModelCategory.ComparisonIntensities) {
      setThresholdOverride(_model?.data.comparisonThreshold || DEFAULT_ANALYSIS_COMPARISON_THRESHOLD);
    }
    setCurrentModel(_model);
  }, []);

  useEffect(() => {
    if (questionningSignature === null) {
      console.log('questioning result widget: null signature');
      setQuestionningResult(null);
      return;
    }
    if (questionningSpotsgrid1d === null) {
      console.log('questioning result widget: null spotsgrid1d');
      return;
    }
    if (currentModel === null) {
      console.log('questioning result widget: received signature upon null model');
      return;
    }

    // classify according to the model type
    if (currentModel?.data.type === ModelCategory.ChemicalPrediction) {
      // console.log('getting chemical prediction questionning spotgrid is', questionningSpotsgrid1d);
      const prediction = classifySignatureChemical(currentModel, questionningSignature, questionningSpotsgrid1d)[0];
      console.log('prediction is', prediction);

      // set in structure to display bubble later
      const packagedData: SecondData[] = [
        {
          id: 'example-id', // You can use any unique ID here
          content: Object.entries(currentModel.data.barycenters || {}).map(([key, value]) => ({
            peptide: key,
            ratio: key === prediction ? 35 : 7, // hardcode found chemical family : size 3 other wise 1.
          })),
        },
      ];
      console.log('packagedData', packagedData);
      setCurrentProba(packagedData);
    } else {
      console.log('other type of model - not chemical');
    }
    let [label, point] = classifySignatureWithModel(currentModel, questionningSignature, questionningSpotsgrid1d);

    let _questionningResult: QuestionningResult = {
      label: label,
      point: point,
    };
    setQuestionningResult(_questionningResult);

    setShowPredictionResult(true);
    setTimeout(() => {
      setShowPredictionResult(false);
    }, 10000);
  }, [questionningSignature, currentModel]);

  const constructSignature = () => {
    if (!currentSpotsgrid1d) {
      console.log('odoridentification page: spotsgrid is empty');
      return;
    }

    // configure preprocessing Pipeline based on model type
    let processingParams = DEFAULT_SIGNATURE_PROCESSING_PARAMS;
    if (currentModel !== null) processingParams = getModelPreprocessingParams(currentModel);

    console.log('Applied processing params:', processingParams);

    // signature with no baseline substraction, simple analyte mean
    let sectionMZIs = analyteMZISeriesRef.current;
    let [processedSignature, processedSpotgrid1d] = computeSignatureFromSignal(sectionMZIs, null, null, currentSpotsgrid1d, processingParams);

    console.log('processed signature', processedSignature);
    console.log('processed spotgrid', processedSpotgrid1d);

    setQuestionningSignature(processedSignature);
    setQuestionningSpotsgrid1d(processedSpotgrid1d);
  };

  return (
    <>
      {/* parameters */}
      <Row justify="end">
        <GlobalParameters showGlobalParameters={showGlobalParameters} setShowGlobalParameters={setShowGlobalParameters} />
        <ResetToZero firstMZIsRef={firstMZIsRef} onClickReset={onClickReset} />
      </Row>

      {!showPredictionResult && questioningStateInfo.current.state === QuestioningState.BaselineRecording && (
        <BaselineRecording MZIvalue={parseFloat(signalEnvelopeAvgRef.current.toFixed(1))} hihValues={{ humidity: humidityDisplayed.current, temperature: temperatureDisplayed.current }} />
      )}
      {!showPredictionResult && questioningStateInfo.current.state === QuestioningState.AnalyteRecording && (
        <AnalyteRecording MZIvalue={parseFloat(signalEnvelopeAvgRef.current.toFixed(1))} hihValues={{ humidity: humidityDisplayed.current, temperature: temperatureDisplayed.current }} />
      )}

      {!showPredictionResult && questioningStateInfo.current.state === QuestioningState.SensorCleaning && (
        <SensorCleaning MZIvalue={parseFloat(signalEnvelopeAvgRef.current.toFixed(1))} hihValues={{ humidity: humidityDisplayed.current, temperature: temperatureDisplayed.current }} />
      )}

      {showPredictionResult && currentModel?.metadata.type === ModelCategory.ChemicalPrediction && (
        <Paper style={{ width: '100%', justifyContent: 'center', alignItems: 'center', marginTop: '20px' }}>
          <BubbleChart data={currentProba} currentChemicalFamily={'toto'} />
        </Paper>
      )}
      {showPredictionResult && currentModel?.metadata.type !== ModelCategory.ChemicalPrediction && <OdorDisplay result={questionningResult} />}

      <GlobalParametersIdentificationComponent
        showGlobalParameters={showGlobalParameters}
        signatureWindowValue={signatureWindowValue}
        setSignatureWindowValue={setSignatureWindowValue}
        thresholdOverride={thresholdOverride}
        setThresholdOverride={setThresholdOverride}
        currentModel={currentModel}
        setCurrentModel={setCurrentModel}
        noizeLevel={questioningStateInfo.current.noizeLevel}
        humidityCompensationEnabled={humidityCompensationEnabled}
        sethumidityCompensationEnabled={sethumidityCompensationEnabled}
      />
    </>
  );
};
